import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  styled,
  SxProps,
  Theme,
} from "@mui/material";
import { type JSX, PropsWithChildren, ReactNode } from "react";
import { Typography } from "@platform/design-system";

type Props = PropsWithChildren<{
  title: ReactNode;
  subtitle?: string;
  action?: React.ReactNode;
  sxContent?: SxProps<Theme>;
  "data-testid"?: string;
}>;

const BaseCard = ({
  title,
  subtitle,
  children,
  action,
  sxContent,
  "data-testid": dataTestId = "ds-base-card",
}: Props): JSX.Element => (
  <Card data-testid={dataTestId}>
    {title || subtitle || action ? (
      <StyledCardHeader
        title={
          title ? (
            <Typography
              variant="display-small"
              data-testid={`${dataTestId}-title`}
              component="h2"
            >
              <strong>{title}</strong>
            </Typography>
          ) : null
        }
        subheader={
          subtitle ? (
            <Typography
              data-testid={`${dataTestId}-subtitle`}
              variant="body-medium"
              color="textSecondary"
            >
              {subtitle}
            </Typography>
          ) : null
        }
        action={
          action ? (
            <div data-testid={`${dataTestId}-action`}>{action}</div>
          ) : null
        }
      />
    ) : null}
    {children ? (
      <>
        <Divider />
        <CardContent sx={sxContent}>{children}</CardContent>
      </>
    ) : null}
  </Card>
);

const StyledCardHeader = styled(CardHeader)({
  "& .MuiCardHeader-action": {
    display: "flex",
    alignItems: "center",
    alignSelf: "center",
    gap: 8,
  },
});

export default BaseCard;
export { BaseCard };
export type { Props as BaseCardProps };
